import { useState, useEffect, useCallback } from 'react'
import { Package, Plus, TrendingUp, DollarSign, Edit } from '@/ui/icons'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { Button } from '@/ui/components'
import { VehicleModification, ModificationCategory, ModificationStats } from '@/shared/api'
import { VehicleModificationApi } from '@/shared/api'
import { useAuth } from '@/modules/auth'
import { AddModificationModal } from './AddModificationModal'

interface VehicleModificationsProfileProps {
  vehicleId: string
  onAddModification?: () => void // Optional callback for parent components
}

export function VehicleModificationsProfile({ vehicleId, onAddModification }: VehicleModificationsProfileProps) {
  const { getAccessToken } = useAuth()
  const [modifications, setModifications] = useState<VehicleModification[]>([])
  const [stats, setStats] = useState<ModificationStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingModification, setEditingModification] = useState<VehicleModification | null>(null)

  const vehicleModificationApi = useCallback(() => new VehicleModificationApi(getAccessToken), [getAccessToken])

  useEffect(() => {
    loadModifications()
    loadStats()
  }, [vehicleId])

  const loadModifications = async () => {
    try {
      setIsLoading(true)
      const api = vehicleModificationApi()
      const data = await api.getVehicleModifications(vehicleId)
      setModifications(data)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const api = vehicleModificationApi()
      const statsData = await api.getModificationStats(vehicleId)
      setStats(statsData)
    } catch (error) {
    }
  }

  const getCategoryLabel = (category: ModificationCategory) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const handleAddClick = () => {
    setEditingModification(null)
    setIsAddModalOpen(true)
    onAddModification?.()
  }

  const handleEditClick = (modification: VehicleModification) => {
    setEditingModification(modification)
    setIsAddModalOpen(true)
  }

  const handleModificationAdded = () => {
    setIsAddModalOpen(false)
    setEditingModification(null)
    loadModifications()
    loadStats()
  }

  if (isLoading) {
    return (
      <Card className="border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-earth-900 dark:text-foreground">
            <Package className="w-5 h-5 text-adventure-600 dark:text-adventure-400" />
            Vehicle Modifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 animate-pulse">
            <div className="h-4 bg-earth-100 dark:bg-earth-200 rounded w-2/3" />
            <div className="h-4 bg-earth-100 dark:bg-earth-200 rounded w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              {[0, 1].map(key => (
                <div
                  key={key}
                  className="p-4 rounded-lg border border-earth-100 dark:border-earth-200 bg-earth-50 dark:bg-earth-100 space-y-3"
                >
                  <div className="h-4 bg-earth-100 dark:bg-earth-200 rounded w-3/4" />
                  <div className="h-3 bg-earth-100 dark:bg-earth-200 rounded w-1/2" />
                  <div className="h-3 bg-earth-100 dark:bg-earth-200 rounded w-full" />
                  <div className="h-3 bg-earth-100 dark:bg-earth-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-border shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-earth-900 dark:text-foreground">
            <Package className="w-5 h-5 text-adventure-600 dark:text-adventure-400" />
            Modifications
            {modifications.length > 0 && (
              <Badge variant="outline">{modifications.length}</Badge>
            )}
          </CardTitle>
          <Button onClick={handleAddClick} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add
          </Button>
        </div>
        {stats && stats.totalModifications > 0 && (
          <div className="flex items-center gap-4 mt-3 text-sm">
            <div className="flex items-center gap-1 text-gray-600 dark:text-earth-600">
              <TrendingUp className="w-4 h-4" />
              <span>{stats.totalModifications} modification{stats.totalModifications !== 1 ? 's' : ''}</span>
            </div>
            {stats.totalInvestment > 0 && (
              <div className="flex items-center gap-1 text-gray-600 dark:text-earth-600">
                <DollarSign className="w-4 h-4" />
                <span>${stats.totalInvestment.toLocaleString('en-US', { maximumFractionDigits: 0 })} invested</span>
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {modifications.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-12 h-12 text-gray-300 dark:text-earth-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-foreground mb-2">No Modifications Yet</h3>
            <p className="text-gray-600 dark:text-earth-600 mb-4 max-w-sm mx-auto">
              Track your vehicle's modifications and gear. Add brands, models, and details to build a complete profile.
            </p>
            <Button onClick={handleAddClick}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Modification
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modifications.map((mod) => (
              <div
                key={mod.id}
                className="p-4 bg-gray-50 dark:bg-earth-100 rounded-lg border border-gray-200 dark:border-earth-200 hover:border-gray-300 dark:hover:bg-earth-200 dark:hover:border-earth-300 transition-colors relative group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-earth-800">{mod.product_name}</h4>
                    <Badge variant="secondary" className="text-xs whitespace-nowrap">
                      {getCategoryLabel(mod.category)}
                    </Badge>
                  </div>
                  {(mod.brand_name || mod.custom_brand_name) && (
                    <div className="flex items-center gap-2 mb-1">
                      {mod.logo_url && (
                        <img
                          src={mod.logo_url}
                          alt={mod.brand_name || mod.custom_brand_name}
                          className="w-4 h-4 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      )}
                      <p className="text-sm text-gray-600 dark:text-earth-600">
                        {mod.brand_name || mod.custom_brand_name}
                        {mod.product_model && ` • ${mod.product_model}`}
                      </p>
                    </div>
                  )}
                  {mod.description && (
                    <p className="text-sm text-gray-500 dark:text-earth-600 mt-1 line-clamp-2">{mod.description}</p>
                  )}
                  {mod.purchase_price && (
                    <p className="text-sm text-gray-600 dark:text-earth-700 mt-2 font-medium">
                      ${mod.purchase_price.toLocaleString()}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 text-earth-800 dark:text-earth-700"
                  onClick={() => handleEditClick(mod)}
                  title="Edit modification"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Modification Modal */}
      <AddModificationModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          setEditingModification(null)
        }}
        vehicleId={vehicleId}
        onModificationAdded={handleModificationAdded}
        editingModification={editingModification}
      />
    </Card>
  )
}

